import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, TBody, Table, Td, Th, THead, Tr } from '../../ui/index.ts';
import { cx } from '../../ui/cx.ts';

/**
 * Generic multi-series SVG trend chart (epic #1643, hover/width UX in
 * #1675) — the reusable extraction of the TrafficMonitorPage TrendPanel
 * idiom, for daily business series (revenue bars, profit lines,
 * per-model lines).
 *
 * Contract:
 *   - `labels` is the shared x-axis (one entry per bucket, typically a
 *     zero-filled `YYYY-MM-DD` business-day list from the API — the
 *     chart never gap-fills);
 *   - every series' `values` aligns index-wise with `labels`;
 *   - all series share ONE value scale (they must be the same unit —
 *     integer CNY cents in the metrics hub);
 *   - colors are Dispatch Deck tone tokens only (`tone` maps to
 *     static Tailwind utility classes — no hex, DESIGN.md §2);
 *   - the chart fills its container: the layout is computed at the
 *     REAL measured pixel width (ResizeObserver), so text renders
 *     1:1 crisp instead of being viewBox-scaled;
 *   - hovering shows a crosshair band on the nearest bucket plus a
 *     floating panel with the bucket label and every series' formatted
 *     value (the old `<title>` fallback is gone — a native tooltip
 *     would fight the custom panel; non-pointer access to the numbers
 *     is the table view, `TrendBucketTable`);
 *   - x labels are sparse (≈7 + the last bucket).
 *
 * Negative values (a loss day) are supported: the baseline is the zero
 * axis and bars/points extend below it.
 */

export type TrendTone =
  | 'signal'
  | 'ok'
  | 'warn'
  | 'bad'
  | 'paper'
  | 'signal-deep'
  | 'bad-bright'
  | 'neutral';

export interface TrendChartSeries {
  readonly key: string;
  readonly label: string;
  readonly kind: 'bar' | 'line';
  readonly tone: TrendTone;
  readonly values: readonly number[];
  /**
   * Per-bucket absolute time for the hover panel. Falls back to the
   * shared `labels` entry when omitted — existing callers keep the
   * axis label as the only time shown.
   */
  readonly bucketLabels?: readonly string[];
  /** Dashed stroke for a comparison series. Default solid. */
  readonly dashed?: boolean;
  /** Fill the area under a line with the series tone. Default off. */
  readonly fillArea?: boolean;
  /**
   * Inclusive last index to draw. Later values stay in the hover
   * dataset but the mark stops — used to cut the current series at
   * "now" without dropping the remaining points to zero.
   */
  readonly drawThroughIndex?: number;
}

/**
 * Pair of series the hover panel should compare: each row carries its
 * own bucket time, a pending current bucket reads as "not yet", and the
 * delta is omitted for pending buckets. Omitted = existing tooltip
 * (shared label + every series' formatted value).
 */
export interface TrendCompareHover {
  readonly currentKey: string;
  readonly previousKey: string;
}

/** Line/legend tone cycle for many-series charts (per-model lines). */
export const TREND_TONE_CYCLE: readonly TrendTone[] = [
  'signal',
  'warn',
  'ok',
  'bad',
  'paper',
  'signal-deep',
  'bad-bright',
  'neutral',
];

const TONE_FILL: Record<TrendTone, string> = {
  signal: 'fill-signal',
  ok: 'fill-ok',
  warn: 'fill-warn',
  bad: 'fill-bad',
  paper: 'fill-paper',
  'signal-deep': 'fill-signal-deep',
  'bad-bright': 'fill-bad-bright',
  neutral: 'fill-paper-mid',
};

const TONE_STROKE: Record<TrendTone, string> = {
  signal: 'stroke-signal',
  ok: 'stroke-ok',
  warn: 'stroke-warn',
  bad: 'stroke-bad',
  paper: 'stroke-paper',
  'signal-deep': 'stroke-signal-deep',
  'bad-bright': 'stroke-bad-bright',
  neutral: 'stroke-paper-mid',
};

const TONE_SWATCH: Record<TrendTone, string> = {
  signal: 'bg-signal',
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  paper: 'bg-paper',
  'signal-deep': 'bg-signal-deep',
  'bad-bright': 'bg-bad-bright',
  neutral: 'bg-paper-mid',
};

/** Pre-measurement width; also the SSR/no-ResizeObserver fallback. */
const FALLBACK_WIDTH = 920;
/** Floor so a squeezed flex column can't degenerate the layout. */
const MIN_WIDTH = 320;
const HEIGHT = 232;
const PADDING_X = 24;
const TOP_Y = 16;
const BOTTOM_Y = 186;
const X_LABEL_Y = 210;

/** Max tooltip rows in interactive-legend mode; the rest collapse into
 * a single "…其余 N 项" line so a 30-series chart stays hoverable. */
const TOOLTIP_MAX_ROWS = 10;

const EMPTY_HIDDEN: ReadonlySet<string> = new Set<string>();

interface Props {
  readonly labels: readonly string[];
  readonly series: readonly TrendChartSeries[];
  readonly valueFormatter: (value: number) => string;
  readonly ariaLabel: string;
  readonly emptyLabel: string;
  readonly testId?: string;
  /**
   * Interactive legend (#1683): series whose keys are in `hiddenKeys`
   * are not drawn, excluded from the y-scale, and omitted from the
   * tooltip. Providing `onToggleSeries` turns the legend chips into
   * toggle buttons (hidden chips render dimmed) and switches the
   * tooltip to value-desc order capped at {@link TOOLTIP_MAX_ROWS}.
   * All three props default off — existing callers are unchanged.
   */
  readonly hiddenKeys?: ReadonlySet<string>;
  readonly onToggleSeries?: (key: string) => void;
  /** Extra legend-row content (e.g. 全选/清空 buttons). */
  readonly legendExtra?: ReactNode;
  /**
   * Fractional bucket coordinate of "now" (0 = left edge of the first
   * bucket, `labels.length` = right edge of the last). Draws a vertical
   * marker and shades the region to the right. Omitted = no marker
   * (existing callers).
   */
  readonly nowAt?: number;
  /**
   * Inclusive last index that has occurred. Hovering a later bucket
   * shows the pending label for the current compare series and omits
   * the delta. Ignored when `compare` is omitted.
   */
  readonly occurredThroughIndex?: number;
  readonly compare?: TrendCompareHover;
  /** Extra mode tag in the hover title (e.g. 逐点 / 累计至此). */
  readonly hoverModeLabel?: string;
  /**
   * Cumulative-mode gap at `nowAt`: vertical bar between the two
   * series' values plus a percent label. Ignored without `nowAt`.
   */
  readonly nowGap?: {
    readonly currentValue: number;
    readonly previousValue: number;
    readonly percentLabel: string;
    readonly tone: 'ok' | 'bad';
  };
}

export function TrendChart({
  labels,
  series,
  valueFormatter,
  ariaLabel,
  emptyLabel,
  testId,
  hiddenKeys,
  onToggleSeries,
  legendExtra,
  nowAt,
  occurredThroughIndex,
  compare,
  hoverModeLabel,
  nowGap,
}: Props): JSX.Element {
  const t = useT();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number | null>(null);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const hasData = labels.length > 0 && series.length > 0;
  const hidden = hiddenKeys ?? EMPTY_HIDDEN;
  const interactive = onToggleSeries !== undefined;
  const visibleSeries = series.filter((s) => !hidden.has(s.key));
  const allHidden = hasData && visibleSeries.length === 0;

  // Real-pixel sizing: observe the container and lay the chart out at
  // its measured width. Guarded so environments without ResizeObserver
  // (or a 0-width first paint) keep the fallback width. Keyed on
  // `hasData`: the empty state renders no container div, so an instance
  // mounted empty has nothing to observe — when data arrives the effect
  // must re-run to attach to the newly-rendered container (a `[]` dep
  // list would leave the chart frozen at the fallback width forever).
  // `allHidden` is a dep for the same reason: the all-hidden state
  // renders no container either.
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!hasData || element === null || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;
      const nextWidth = Math.round(entry.contentRect.width);
      if (nextWidth > 0) {
        setMeasuredWidth((previous) => (previous === nextWidth ? previous : nextWidth));
      }
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [hasData, allHidden]);

  if (!hasData) {
    return (
      <p className="m-0 p-4 text-body text-paper-low" data-testid={testId !== undefined ? `${testId}-empty` : undefined}>
        {emptyLabel}
      </p>
    );
  }

  const legend = (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {series.map((s) => {
        const swatch = (
          <span
            aria-hidden="true"
            className={cx(
              s.kind === 'bar' ? 'h-2.5 w-2.5 rounded-sm' : 'h-0.5 w-3.5 rounded-full',
              TONE_SWATCH[s.tone],
              s.dashed === true ? 'opacity-70' : undefined,
            )}
          />
        );
        if (onToggleSeries === undefined) {
          return (
            <span key={s.key} className="flex items-center gap-1.5 text-label text-paper-mid">
              {swatch}
              <span className="font-mono">{s.label}</span>
            </span>
          );
        }
        const isHidden = hidden.has(s.key);
        return (
          <button
            key={s.key}
            type="button"
            aria-pressed={!isHidden}
            data-testid={testId !== undefined ? `${testId}-legend-${s.key}` : undefined}
            onClick={() => {
              onToggleSeries(s.key);
            }}
            className={cx(
              'flex items-center gap-1.5 text-label transition-opacity duration-120',
              isHidden ? 'text-paper-low opacity-40 hover:opacity-70' : 'text-paper-mid hover:text-paper',
            )}
          >
            {swatch}
            <span className="font-mono">{s.label}</span>
          </button>
        );
      })}
      {legendExtra}
    </div>
  );

  if (allHidden) {
    return (
      <div className="flex flex-col gap-3" data-testid={testId}>
        {legend}
        <p
          className="m-0 rounded-lg bg-well p-4 text-body text-paper-low"
          data-testid={testId !== undefined ? `${testId}-all-hidden` : undefined}
        >
          {t('metrics.chart.all_hidden')}
        </p>
      </div>
    );
  }

  const width = Math.max(MIN_WIDTH, measuredWidth ?? FALLBACK_WIDTH);
  // Scale over VISIBLE series only — hiding a dominant series rescales
  // the remaining lines instead of leaving them squashed at the bottom.
  const allValues = visibleSeries.flatMap((s) => s.values.slice(0, labels.length));
  // Top of the scale clamps to 0 for an all-negative window (a fake
  // positive ceiling would label the top gridline with a value no data
  // reaches); an all-zero window degenerates to span 1 so yFor stays
  // finite.
  const maxValue = Math.max(0, ...allValues);
  const minValue = Math.min(0, ...allValues);
  const span = maxValue - minValue || 1;
  const chartHeight = BOTTOM_Y - TOP_Y;
  const yFor = (value: number): number => BOTTOM_Y - ((value - minValue) / span) * chartHeight;
  const zeroY = yFor(0);
  const midValue = minValue + span / 2;

  const groupWidth = (width - PADDING_X * 2) / labels.length;
  const barSeries = visibleSeries.filter((s) => s.kind === 'bar');
  const lineSeries = visibleSeries.filter((s) => s.kind === 'line');
  const barWidth = Math.max(2, (groupWidth * 0.6) / Math.max(1, barSeries.length));
  const labelEvery = Math.max(1, Math.ceil(labels.length / 7));

  const handleMouseMove = (event: ReactMouseEvent<SVGSVGElement>): void => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const pointerX = event.clientX - bounds.left;
    const rawIndex = Math.floor((pointerX - PADDING_X) / groupWidth);
    const nextIndex = Math.min(labels.length - 1, Math.max(0, rawIndex));
    setHoverIndex((previous) => (previous === nextIndex ? previous : nextIndex));
  };

  const hoverCenterX =
    hoverIndex === null ? 0 : PADDING_X + hoverIndex * groupWidth + groupWidth / 2;
  // The floating panel hangs beside the hovered bucket, flipping sides
  // at the midline so it always stays inside the container.
  const tooltipStyle =
    hoverCenterX <= width / 2
      ? { left: `${String(Math.round(hoverCenterX + 12))}px` }
      : { right: `${String(Math.round(width - hoverCenterX + 12))}px` };

  // Interactive-legend mode sorts the tooltip by the hovered bucket's
  // value (desc) and caps the rows; the classic mode keeps declaration
  // order and lists everything (small fixed series sets).
  const tooltipRows =
    hoverIndex === null
      ? visibleSeries
      : interactive
        ? [...visibleSeries].sort(
            (a, b) => (b.values[hoverIndex] ?? 0) - (a.values[hoverIndex] ?? 0),
          )
        : visibleSeries;
  const tooltipOverflow = interactive ? Math.max(0, tooltipRows.length - TOOLTIP_MAX_ROWS) : 0;
  const shownTooltipRows = tooltipOverflow > 0 ? tooltipRows.slice(0, TOOLTIP_MAX_ROWS) : tooltipRows;

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      {legend}
      <div ref={containerRef} className="relative w-full">
        <svg
          role="img"
          aria-label={ariaLabel}
          data-testid={testId !== undefined ? `${testId}-svg` : undefined}
          width={width}
          height={HEIGHT}
          viewBox={`0 0 ${String(width)} ${String(HEIGHT)}`}
          className="block rounded-lg bg-well"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => {
            setHoverIndex(null);
          }}
        >
          {/* gridlines + y labels */}
          <line x1={PADDING_X} x2={width - PADDING_X} y1={TOP_Y} y2={TOP_Y} strokeWidth={1} className="stroke-line-soft" />
          <line x1={PADDING_X} x2={width - PADDING_X} y1={yFor(midValue)} y2={yFor(midValue)} strokeWidth={1} className="stroke-line-soft" />
          <line x1={PADDING_X} x2={width - PADDING_X} y1={zeroY} y2={zeroY} strokeWidth={1} className="stroke-line-strong" />
          <text x={PADDING_X + 2} y={TOP_Y - 4} className="fill-paper-low font-mono text-micro">
            {valueFormatter(maxValue)}
          </text>
          {/* Skip the zero label when the zero axis IS the top axis
              (all-negative window) — it would duplicate the label above. */}
          {zeroY - TOP_Y > 8 ? (
            <text x={PADDING_X + 2} y={zeroY - 4} className="fill-paper-low font-mono text-micro">
              {valueFormatter(0)}
            </text>
          ) : null}
          {nowAt !== undefined ? (
            <rect
              data-testid={testId !== undefined ? `${testId}-future-shade` : undefined}
              x={Math.min(width - PADDING_X, Math.max(PADDING_X, PADDING_X + nowAt * groupWidth))}
              y={TOP_Y}
              width={Math.max(0, width - PADDING_X - (PADDING_X + nowAt * groupWidth))}
              height={BOTTOM_Y - TOP_Y}
              fillOpacity={0.72}
              className="fill-canvas"
            />
          ) : null}
          {/* hovered-bucket band + center guide, under the marks */}
          {hoverIndex !== null ? (
            <g aria-hidden="true">
              <rect
                data-testid={testId !== undefined ? `${testId}-hover-band` : undefined}
                x={PADDING_X + hoverIndex * groupWidth}
                y={TOP_Y}
                width={groupWidth}
                height={BOTTOM_Y - TOP_Y}
                fillOpacity={0.55}
                className="fill-active"
              />
              <line x1={hoverCenterX} x2={hoverCenterX} y1={TOP_Y} y2={BOTTOM_Y} strokeWidth={1} className="stroke-line-strong" />
            </g>
          ) : null}
          {/* per-bucket bars */}
          {labels.map((label, index) => {
            const x0 = PADDING_X + index * groupWidth;
            return (
              <g key={label} data-testid={testId !== undefined ? `${testId}-bucket-${index}` : undefined}>
                {barSeries.map((s, barIndex) => {
                  const value = s.values[index] ?? 0;
                  if (value === 0) return null;
                  const y = yFor(value);
                  const barX = x0 + groupWidth * 0.2 + barIndex * barWidth;
                  const top = Math.min(y, zeroY);
                  const height = Math.max(1, Math.abs(zeroY - y));
                  return (
                    <rect
                      key={s.key}
                      x={barX}
                      y={top}
                      width={barWidth}
                      height={height}
                      rx={1}
                      fillOpacity={0.8}
                      className={TONE_FILL[s.tone]}
                    />
                  );
                })}
                {index % labelEvery === 0 || index === labels.length - 1 ? (
                  <text
                    x={x0 + groupWidth / 2}
                    y={X_LABEL_Y}
                    textAnchor="middle"
                    className="fill-paper-low font-mono text-micro"
                  >
                    {shortLabel(label)}
                  </text>
                ) : null}
              </g>
            );
          })}
          {/* line series drawn above the bars; a single-bucket window
              gets a circle marker — a one-point polyline paints nothing. */}
          {lineSeries.map((s) => {
            const lastIndex = lastDrawnIndex(s, labels.length);
            if (lastIndex < 0) return null;
            const drawnCount = lastIndex + 1;
            if (drawnCount === 1) {
              return (
                <circle
                  key={s.key}
                  data-testid={testId !== undefined ? `${testId}-line-${s.key}` : undefined}
                  cx={PADDING_X + groupWidth / 2}
                  cy={yFor(s.values[0] ?? 0)}
                  r={3}
                  className={TONE_FILL[s.tone]}
                />
              );
            }
            const pointPairs = Array.from({ length: drawnCount }, (_, index) => {
              const x = PADDING_X + index * groupWidth + groupWidth / 2;
              const y = yFor(s.values[index] ?? 0);
              return { x, y };
            });
            const points = pointPairs.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
            const first = pointPairs[0];
            const last = pointPairs[pointPairs.length - 1];
            return (
              <g key={s.key}>
                {s.fillArea === true && first !== undefined && last !== undefined ? (
                  <polygon
                    points={`${first.x.toFixed(1)},${zeroY.toFixed(1)} ${points} ${last.x.toFixed(1)},${zeroY.toFixed(1)}`}
                    fillOpacity={0.1}
                    className={TONE_FILL[s.tone]}
                  />
                ) : null}
                <polyline
                  data-testid={testId !== undefined ? `${testId}-line-${s.key}` : undefined}
                  points={points}
                  fill="none"
                  strokeWidth={s.dashed === true ? 1.6 : 2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={s.dashed === true ? '5 4' : undefined}
                  className={TONE_STROKE[s.tone]}
                />
              </g>
            );
          })}
          {nowAt !== undefined ? (
            <g aria-hidden="true">
              <line
                data-testid={testId !== undefined ? `${testId}-now-line` : undefined}
                x1={PADDING_X + nowAt * groupWidth}
                x2={PADDING_X + nowAt * groupWidth}
                y1={TOP_Y}
                y2={BOTTOM_Y}
                strokeWidth={1.2}
                strokeDasharray="3 3"
                className="stroke-line-strong"
              />
              <text
                x={PADDING_X + nowAt * groupWidth - 6}
                y={TOP_Y + 10}
                textAnchor="end"
                className="fill-paper-low font-mono text-micro"
              >
                {t('metrics.chart.now')}
              </text>
              {nowGap !== undefined ? (
                <>
                  <line
                    data-testid={testId !== undefined ? `${testId}-now-gap` : undefined}
                    x1={PADDING_X + nowAt * groupWidth}
                    x2={PADDING_X + nowAt * groupWidth}
                    y1={yFor(nowGap.currentValue)}
                    y2={yFor(nowGap.previousValue)}
                    strokeWidth={2.5}
                    strokeOpacity={0.8}
                    className={nowGap.tone === 'ok' ? 'stroke-ok' : 'stroke-bad'}
                  />
                  <text
                    x={PADDING_X + nowAt * groupWidth - 8}
                    y={(yFor(nowGap.currentValue) + yFor(nowGap.previousValue)) / 2 + 3}
                    textAnchor="end"
                    className={cx(
                      'font-mono text-label',
                      nowGap.tone === 'ok' ? 'fill-ok' : 'fill-bad',
                    )}
                  >
                    {nowGap.percentLabel}
                  </text>
                </>
              ) : null}
            </g>
          ) : null}
        </svg>
        {hoverIndex !== null ? (
          <div
            data-testid={testId !== undefined ? `${testId}-tooltip` : undefined}
            style={tooltipStyle}
            className={cx(
              'pointer-events-none absolute top-2 z-10 rounded border border-line bg-raised px-2.5 py-1.5 shadow-raised',
              compare !== undefined ? 'min-w-48' : 'min-w-[132px]',
            )}
          >
            <TrendHoverPanel
              hoverIndex={hoverIndex}
              labels={labels}
              shownRows={shownTooltipRows}
              valueFormatter={valueFormatter}
              compare={compare}
              occurredThroughIndex={occurredThroughIndex}
              hoverModeLabel={hoverModeLabel}
              pendingLabel={t('metrics.chart.hover.pending')}
              deltaLabel={t('metrics.chart.hover.delta')}
              moreLabel={
                tooltipOverflow > 0 ? t('metrics.chart.tooltip_more', { count: tooltipOverflow }) : null
              }
              testId={testId}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function lastDrawnIndex(series: TrendChartSeries, labelCount: number): number {
  if (labelCount <= 0) return -1;
  const cap = series.drawThroughIndex ?? labelCount - 1;
  return Math.min(labelCount - 1, Math.max(-1, cap));
}

function formatDeltaPercent(current: number, previous: number): string | null {
  if (!(previous > 0) || !Number.isFinite(current) || !Number.isFinite(previous)) {
    return null;
  }
  const pct = (current / previous - 1) * 100;
  if (!Number.isFinite(pct)) return null;
  const abs = Math.abs(pct).toFixed(1);
  return pct >= 0 ? `+${abs}%` : `-${abs}%`;
}

function TrendHoverPanel({
  hoverIndex,
  labels,
  shownRows,
  valueFormatter,
  compare,
  occurredThroughIndex,
  hoverModeLabel,
  pendingLabel,
  deltaLabel,
  moreLabel,
  testId,
}: {
  readonly hoverIndex: number;
  readonly labels: readonly string[];
  readonly shownRows: readonly TrendChartSeries[];
  readonly valueFormatter: (value: number) => string;
  readonly compare: TrendCompareHover | undefined;
  readonly occurredThroughIndex: number | undefined;
  readonly hoverModeLabel: string | undefined;
  readonly pendingLabel: string;
  readonly deltaLabel: string;
  readonly moreLabel: string | null;
  readonly testId: string | undefined;
}): JSX.Element {
  const axisLabel = labels[hoverIndex] ?? '';
  const currentSeries =
    compare === undefined ? undefined : shownRows.find((row) => row.key === compare.currentKey);
  const titleLabel = currentSeries?.bucketLabels?.[hoverIndex] ?? axisLabel;
  const pending =
    occurredThroughIndex !== undefined && hoverIndex > occurredThroughIndex;
  const currentValue = currentSeries?.values[hoverIndex];
  const previousSeries =
    compare === undefined ? undefined : shownRows.find((row) => row.key === compare.previousKey);
  const previousValue = previousSeries?.values[hoverIndex];
  const delta =
    compare !== undefined && !pending && currentValue !== undefined && previousValue !== undefined
      ? formatDeltaPercent(currentValue, previousValue)
      : null;

  return (
    <>
      <div
        data-testid={testId !== undefined ? `${testId}-tooltip-title` : undefined}
        className={cx(
          'font-mono text-micro text-paper',
          compare !== undefined ? 'border-b border-line-soft pb-1' : undefined,
        )}
      >
        {titleLabel}
        {hoverModeLabel !== undefined ? (
          <span className="font-sans font-normal text-paper-low"> · {hoverModeLabel}</span>
        ) : null}
      </div>
      {shownRows.map((s) => {
        const isCurrent = compare !== undefined && s.key === compare.currentKey;
        const showPending = isCurrent && pending;
        const bucketTime = s.bucketLabels?.[hoverIndex];
        const showBucketTime = compare !== undefined && !isCurrent && bucketTime !== undefined && bucketTime !== titleLabel;
        return (
          <div
            key={s.key}
            data-testid={testId !== undefined ? `${testId}-tooltip-row-${s.key}` : undefined}
            className="flex items-center justify-between gap-3 text-micro text-paper-mid"
          >
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={cx(
                  s.kind === 'bar' ? 'h-2 w-2 rounded-sm' : 'h-0.5 w-3 rounded-full',
                  TONE_SWATCH[s.tone],
                )}
              />
              <span>{s.label}</span>
              {showBucketTime ? <span className="text-paper-low">{bucketTime}</span> : null}
            </span>
            {showPending ? (
              <span
                data-testid={testId !== undefined ? `${testId}-tooltip-pending` : undefined}
                className="font-mono text-paper-low"
              >
                {pendingLabel}
              </span>
            ) : (
              <span className="font-mono text-paper">{valueFormatter(s.values[hoverIndex] ?? 0)}</span>
            )}
          </div>
        );
      })}
      {delta !== null ? (
        <div
          data-testid={testId !== undefined ? `${testId}-tooltip-delta` : undefined}
          className="mt-0.5 flex items-center justify-between gap-3 border-t border-line-soft pt-0.5 text-micro"
        >
          <span className="text-paper-mid">{deltaLabel}</span>
          <span className={cx('font-mono', delta.startsWith('+') ? 'text-ok' : 'text-bad')}>{delta}</span>
        </div>
      ) : null}
      {moreLabel !== null ? <div className="text-micro text-paper-low">{moreLabel}</div> : null}
    </>
  );
}

/** `2026-08-04` → `08-04`; anything else passes through untouched. */
function shortLabel(label: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(label) ? label.slice(5) : label;
}

// ─── chart/table view + rollup (#1675) ───────────────────────────────

export type TrendView = 'chart' | 'table';
export type TrendRollup = 'day' | 'week' | 'month';

export interface RolledTrendBuckets {
  readonly labels: readonly string[];
  readonly series: readonly (readonly number[])[];
}

const DAY_LABEL_RE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

/**
 * ISO-8601 week label (`2026-W32`) for a `YYYY-MM-DD` day. Weeks are
 * Monday-started natural ISO weeks — NOT 7-day buckets aligned to the
 * query window — so a bucket at the window edge may cover fewer than 7
 * loaded days (the table's rollup note states this).
 */
function isoWeekLabel(label: string): string {
  const dayIndex = Math.floor(Date.parse(`${label}T00:00:00Z`) / MS_PER_DAY);
  const weekday = (dayIndex + 3) % 7; // Monday = 0 (1970-01-01 was a Thursday)
  const mondayIndex = dayIndex - weekday;
  const thursdayIndex = mondayIndex + 3; // ISO year = year of the week's Thursday
  const isoYear = new Date(thursdayIndex * MS_PER_DAY).getUTCFullYear();
  const jan4Index = Math.floor(Date.UTC(isoYear, 0, 4) / MS_PER_DAY);
  const week1MondayIndex = jan4Index - ((jan4Index + 3) % 7);
  const week = 1 + (mondayIndex - week1MondayIndex) / 7;
  return `${String(isoYear)}-W${String(week).padStart(2, '0')}`;
}

/**
 * Client-side rollup of aligned daily buckets: `week` groups by ISO
 * week, `month` by calendar month (the daily labels are already UTC+8
 * business days, so slicing the label IS the UTC+8 calendar month).
 * Values are summed per group; chronological order is preserved.
 * Non-`YYYY-MM-DD` labels group as themselves (identity).
 */
export function rollupDailyBuckets(
  labels: readonly string[],
  series: readonly (readonly number[])[],
  rollup: TrendRollup,
): RolledTrendBuckets {
  if (rollup === 'day') {
    return { labels, series };
  }
  const keyFor = (label: string): string => {
    if (!DAY_LABEL_RE.test(label)) return label;
    return rollup === 'week' ? isoWeekLabel(label) : label.slice(0, 7);
  };
  const outLabels: string[] = [];
  const outSeries: number[][] = series.map(() => []);
  const groupIndexByKey = new Map<string, number>();
  labels.forEach((label, index) => {
    const key = keyFor(label);
    let group = groupIndexByKey.get(key);
    if (group === undefined) {
      group = outLabels.length;
      groupIndexByKey.set(key, group);
      outLabels.push(key);
      for (const sums of outSeries) {
        sums.push(0);
      }
    }
    series.forEach((values, seriesIndex) => {
      const sums = outSeries[seriesIndex];
      if (sums !== undefined) {
        sums[group] = (sums[group] ?? 0) + (values[index] ?? 0);
      }
    });
  });
  return { labels: outLabels, series: outSeries };
}

const ROLLUP_OPTIONS: readonly TrendRollup[] = ['day', 'week', 'month'];
const ROLLUP_KEYS: Record<TrendRollup, I18nKey> = {
  day: 'metrics.chart.rollup.day',
  week: 'metrics.chart.rollup.week',
  month: 'metrics.chart.rollup.month',
};
const VIEW_OPTIONS: readonly TrendView[] = ['chart', 'table'];
const VIEW_KEYS: Record<TrendView, I18nKey> = {
  chart: 'metrics.chart.view.chart',
  table: 'metrics.chart.view.table',
};

interface TrendViewToggleProps {
  readonly view: TrendView;
  readonly onViewChange: (view: TrendView) => void;
  readonly rollup: TrendRollup;
  readonly onRollupChange: (rollup: TrendRollup) => void;
  readonly testIdPrefix: string;
  /**
   * Hide the 日/周/月 rollup pills entirely (#1675): hour-granularity
   * series have no day buckets to roll up, so offering the pills would
   * be a no-op lie. Defaults to true (daily series).
   */
  readonly showRollup?: boolean;
}

/**
 * 图表/表格 switch + the 日/周/月 rollup pills (rollup only shows in
 * table view — the chart always draws the daily buckets).
 */
export function TrendViewToggle({
  view,
  onViewChange,
  rollup,
  onRollupChange,
  testIdPrefix,
  showRollup = true,
}: TrendViewToggleProps): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-wrap items-center gap-2">
      {view === 'table' && showRollup ? (
        <div role="tablist" aria-label={t('metrics.chart.rollup_aria')} className="inline-flex gap-1">
          {ROLLUP_OPTIONS.map((option) => {
            const active = option === rollup;
            return (
              <Button
                key={option}
                role="tab"
                aria-selected={active}
                variant={active ? 'secondary' : 'ghost'}
                size="sm"
                className={active ? 'bg-active text-paper' : undefined}
                data-testid={`${testIdPrefix}-rollup-${option}`}
                onClick={() => {
                  onRollupChange(option);
                }}
              >
                {t(ROLLUP_KEYS[option])}
              </Button>
            );
          })}
        </div>
      ) : null}
      <div role="tablist" aria-label={t('metrics.chart.view_aria')} className="inline-flex gap-1">
        {VIEW_OPTIONS.map((option) => {
          const active = option === view;
          return (
            <Button
              key={option}
              role="tab"
              aria-selected={active}
              variant={active ? 'secondary' : 'ghost'}
              size="sm"
              className={active ? 'bg-active text-paper' : undefined}
              data-testid={`${testIdPrefix}-view-${option}`}
              onClick={() => {
                onViewChange(option);
              }}
            >
              {t(VIEW_KEYS[option])}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

export interface TrendTableColumn {
  readonly key: string;
  readonly header: string;
  /** Pre-formatted display cells, aligned index-wise with `labels`. */
  readonly cells: readonly string[];
}

interface TrendBucketTableProps {
  readonly bucketHeader: string;
  readonly labels: readonly string[];
  readonly columns: readonly TrendTableColumn[];
  readonly emptyLabel: string;
  readonly note?: string;
  readonly testId?: string;
}

/**
 * The table twin of `TrendChart`: one row per (possibly rolled-up)
 * bucket, numeric columns right-aligned mono per DESIGN.md §6. Cells
 * arrive pre-formatted — the caller owns units and derived columns.
 */
export function TrendBucketTable({
  bucketHeader,
  labels,
  columns,
  emptyLabel,
  note,
  testId,
}: TrendBucketTableProps): JSX.Element {
  if (labels.length === 0) {
    return (
      <p className="m-0 text-body text-paper-low" data-testid={testId !== undefined ? `${testId}-empty` : undefined}>
        {emptyLabel}
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <Table>
        <THead>
          <Tr interactive={false}>
            <Th>{bucketHeader}</Th>
            {columns.map((column) => (
              <Th key={column.key} num>
                {column.header}
              </Th>
            ))}
          </Tr>
        </THead>
        <TBody>
          {labels.map((label, rowIndex) => (
            <Tr key={label} data-testid={testId !== undefined ? `${testId}-row-${rowIndex}` : undefined}>
              <Td mono emphasis>
                {label}
              </Td>
              {columns.map((column) => (
                <Td key={column.key} num>
                  {column.cells[rowIndex] ?? '—'}
                </Td>
              ))}
            </Tr>
          ))}
        </TBody>
      </Table>
      {note !== undefined ? <p className="m-0 text-micro text-paper-low">{note}</p> : null}
    </div>
  );
}
